<script setup lang="ts">
import { ref, watch, computed, reactive } from 'vue';
import { useQuery } from '@tanstack/vue-query'
import { useAssetSettings, useAssetSettingsLookups } from '@/composables/assets/assets';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import Card from "@/components/Card/card-container.vue";
import CardContent from "@/components/Card/card-content.vue";
import CardHeader from "@/components/Card/card-header.vue";
import CardHeaderItem from "@/components/Card/card-header-item.vue";
import InputSelect from "@/components/input-select.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import InputDate from "@/components/input-date.vue";
import InputNumber from "@/components/input-number.vue";
import InputCheckbox from "@/components/input-checkbox.vue";
import GridContainer from "@/components/grid-container.vue";
import GridItem from "@/components/grid-item.vue";
import InputLabel from "@/components/input-label.vue";
import ContentLoader from "@/components/content-loader.vue";
import { type BillingSettings } from "@/api/assets/schemas";
import { useProjectTags } from '@/composables/assets/customers'
import InputToggleGroup from '@/components/input-toggle-group.vue'
import { MeterBillingType } from '@/api/assets/constants'
import { AssetType } from '@/features/assets/constants'
import TooltipIcon from '@/shared/components/TooltipIcon.vue'
import { assetQueries } from '@/features/assets/queries'
import { format } from 'date-fns/format'

const props = defineProps<{ assetCode: string }>();
const emit = defineEmits<{
  (event: 'update:autoApprove', autoApprove: boolean): void
}>();
const { data: assetDetails } = useQuery(assetQueries.details(props.assetCode))
const { data: assetData, isLoading: asseetLoading } = useAssetSettings(props.assetCode);
const { data: lookups, isLoading: lookupsLoading } = useAssetSettingsLookups();
const { data: projectTags, isLoading: projectTagsAreLoading } = useProjectTags(() => assetData.value?.invoiceDetails.contractFundEntity?.id)

const billingCycleId = ref<number>();
const isLoading = computed(() => asseetLoading.value || lookupsLoading.value)
const isAutoApproving = ref<boolean>()

watch(assetData, (newAssetData) => {
  if (newAssetData?.billingSettings === undefined) {
    return
  }
  isAutoApproving.value = newAssetData.billingSettings?.autoApprove
  billingCycleId.value = newAssetData.billingSettings?.billingCycle?.id
}, { immediate: true });

watch(
  isAutoApproving,
  newValue => emit('update:autoApprove', newValue!),
  { immediate: true }
)

</script>
<template>
  <Card class="billing-settings-card">
    <CardHeader>
      <CardHeaderItem>Billing Settings</CardHeaderItem>
    </CardHeader>
    <CardContent>
      <ContentLoader :loading="isLoading" variant="overlay">
        <GridContainer :columns="[184, '1fr']" :column-gap="20" :class="'card-base-grid'" :row-gap="10"
          :align="'center'">

          <GridItem>
            <InputLabel>Virtual Solar Data</InputLabel>
          </GridItem>
          <GridItem>
            <InputToggleGroup
              name="billingSettings.meterBillingType"
              :options="[{ text: 'Actual', value: MeterBillingType.Actual }, { text: 'Virtual', value: MeterBillingType.Virtual }]"
              :selected="assetData?.billingSettings?.meterBillingType"
            />
          </GridItem>

          <GridItem>
            <InputLabel>Virtual Grid Data</InputLabel>
          </GridItem>
          <GridItem>
            <InputToggleGroup name="billingSettings.gridMeterBillingType"
            :options="[{text: 'Actual', value: MeterBillingType.Actual},{text: 'Virtual', value: MeterBillingType.Virtual }]"
            :selected="assetData?.billingSettings?.gridMeterBillingType"/>
          </GridItem>

          <GridItem>
            <InputLabel>Generate Monthly Solar Report
            <FontAwesomeIcon class="tooltip-color" :icon="faQuestionCircle"
            v-tooltip="`Controls whether the monthly solar report is generated with the invoice. If set to ‘No’ and later required, cancel the invoice, switch to ‘Yes,’ and regenerate the invoice`"/>
          </InputLabel>
          </GridItem>
          <GridItem>
            <InputToggleGroup
              name="billingSettings.enableMonthlySolarReport"
              :options="[{text: 'Yes', value: true}, {text: 'No', value: false}]"
              :selected="assetData?.billingSettings?.enableMonthlySolarReport"
            />
          </GridItem>

          <GridItem>
            <InputLabel>Customer Tag</InputLabel>
          </GridItem>
          <GridItem>
            <InputSelect name="billingSettings.customerTag"
                         :items="projectTags?.options.map(tag => ({ text: tag.name, value: tag.id })) ?? []"
                         item-text="text"
                         item-value="value"
                         searchable
                         required
                         :selected="assetData?.billingSettings?.customerTag"
                         error-variant="tooltip"
                         :placeholder="projectTagsAreLoading ? 'Loading...' : undefined" />
          </GridItem>

          <template v-if="assetDetails?.type === AssetType.Wheeling">
            <InputLabel>Generation Site(s)</InputLabel>
            <div>{{ assetData?.billingSettings?.wheelingPowerPlants ?? '-' }}</div>

            <InputLabel>Wheeling Project Code</InputLabel>
            <div>{{ assetData?.billingSettings?.wheelingProjectCode ?? '-' }}</div>

            <InputLabel>Billing Source System</InputLabel>
            <div>{{ assetData?.billingSettings?.wheelingSourceSystem ?? '-' }}</div>

            <InputLabel class="align-self-start">Month Status</InputLabel>
            <div v-html="
              assetData?.billingSettings.wheelingInvoiceGeneration?.map(
                x => `${format(new Date(x.period), `MMM yyyy`)} (${x.enabled === null ? '-' : (x.enabled ? 'Closed' : 'Open') })`
              ).join('<br>') ?? '-'
            "></div>
          </template>

          <GridItem>
            <InputLabel>
              Commissioning Date
              <TooltipIcon text="The date from which the asset was commissioned"  class="ms-1" placement="bottom"/>
            </InputLabel>
          </GridItem>

          <GridItem>
            <InputDate
              placeholder="Date"
              :value="assetData?.billingSettings?.commissioningDate" :name="'billingSettings.commissioningDate'"
            />
          </GridItem>

          <GridItem>
            <InputLabel>
              Invoice Start Date
            </InputLabel>
          </GridItem>

          <GridItem>
            <InputDate
              placeholder="Date"
              :value="assetData?.billingSettings?.invoiceStartDate" :name="'billingSettings.invoiceStartDate'"
            />
          </GridItem>

          <GridItem>
            <InputLabel>Invoice Date Format</InputLabel>
          </GridItem>
          <GridItem>
            <InputSelect type="text" placeholder="Please select" item-text="name" item-value="name"
                         name="billingSettings.invoiceDateFormat"
               :items="lookups?.invoiceDateFormats || []" :selected="assetData?.billingSettings?.invoiceDateFormat"
              error-variant="tooltip" />
          </GridItem>

          <GridItem>
            <InputLabel>Billing Cycle</InputLabel>
            <FontAwesomeIcon class="tooltip-color ms-1" :icon="faQuestionCircle" v-tooltip="`Choose the billing period<br />
            for the customer. Results<br />
            span from the 1st to the<br />
            last day of the month.`" />
          </GridItem>
          <GridItem>
            <InputSelect type="text" placeholder="Please select" item-text="name" item-value="id"
                         name="billingSettings.billingCycleId"
              :disabled="true" :items="lookups?.billingCycles || []" :selected="assetData?.billingSettings?.billingCycle?.id"
              error-variant="tooltip" />
          </GridItem>
          <GridItem>
            <InputLabel>Invoice Date</InputLabel>
            <FontAwesomeIcon class="tooltip-color ms-1" :icon="faQuestionCircle" v-tooltip="`The invoice is generated<br />
            on this day every month.<br />
            This will also reflect as the<br />
            Invoice Date on the Invoice.`" />
          </GridItem>
          <GridItem>
            <p>1st day of each Month</p>
          </GridItem>
          <GridItem>
            <InputLabel>Payment Due Within</InputLabel>
          </GridItem>
          <GridItem>
            <div class="d-flex gap-2 align-items-center">
              <InputNumber placeholder="0" style="width: 62px; flex: none" :value="assetData?.billingSettings?.paymentDueInDays"
                required :step="1" error-variant="tooltip"
                :name="'billingSettings.paymentDueInDays'" />
              Days
            </div>
          </GridItem>
          <GridItem>
            <InputLabel>Auto Approve</InputLabel>
          </GridItem>
          <GridItem>
            <InputCheckbox v-model:checked="isAutoApproving" name="billingSettings.autoApprove" error-variant="tooltip" />
          </GridItem>
          <template v-if="isAutoApproving">
            <GridItem>
              <InputLabel>Expected Energy Variance</InputLabel>
              <FontAwesomeIcon class="tooltip-color ms-1" :icon="faQuestionCircle" v-tooltip="`
              % variance between daily<br />
              average for the invoice period<br />
              vs daily average over the<br/>
              preceding 3 months`" />
            </GridItem>
            <GridItem>
              <InputNumber style="width: 115px; flex: none" class="medium-input input-percentage" placeholder="0"
                :value="assetData?.billingSettings?.expectedEnergyVariance" :name="'billingSettings.expectedEnergyVariance'" error-variant="tooltip">
                <template #suffix>%</template>
              </InputNumber>
            </GridItem>
            <GridItem>
              <InputLabel>Grid Export Validation</InputLabel>
              <FontAwesomeIcon class="tooltip-color ms-1" :icon="faQuestionCircle" v-tooltip="`If more than 3% of generated<br />
              solar energy was exported,<br />
              invoice will not be auto-approved.`" />
            </GridItem>
            <GridItem>
              <InputCheckbox :checked="assetData?.billingSettings?.gridExportValidation" name="billingSettings.gridExportValidation"
                error-variant="tooltip" />
            </GridItem>
          </template>
        </GridContainer>
      </ContentLoader>
    </CardContent>
  </Card>
</template>
<style scoped lang="scss">
.billing-settings-card {
  min-height: 415px;

  .card-base-grid {
    align-items: baseline;
    color: #6d6e71;
  }

  .card-input-title {
    font-size: 12px;
    font-weight: bold;
    color: #6d6e71;
  }
}

.first-escalation-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  height: 28px;
}

.medium-input {
  width: 115px;
}

.input-currency {
  :deep(.form-control) {
    text-align: right !important;
  }

  :deep(input) {
    border-left: none;
    text-align: right;
  }

  :deep(.input-group-text) {
    color: #d1d1d1;
    background-color: #fff !important;
  }

  :deep(.input-group) {
    width: unset !important;
  }
}

.input-percentage {
  :deep(.form-control) {
    text-align: left;
  }

  :deep(input) {
    border-right: none;
  }

  :deep(.input-group-text) {
    color: #d1d1d1;
    background-color: #fff !important;
  }
}

.tooltip-color {
  color: #d1d1d1;
}
</style>
